// src/context/SubscriptionContext.jsx

import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { doc, onSnapshot, setDoc, updateDoc, serverTimestamp, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export function useSubscription() {
  return useContext(SubscriptionContext);
}

// Subscription plans
export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: 'free',
    name: 'Ücretsiz',
    price: 0,
    duration: 'monthly',
    features: [
      '10 sipariş/ay',
      '5 müşteri',
      'Temel raporlar',
      'Email destek',
      'Sadece metin PDF'
    ],
    limits: {
      orders: 10,
      customers: 5,
      products: 50,
      invoices: 10
    },
    restrictions: {
      images: false,
      pdfImages: false,
      advancedReports: false,
      excelExport: false,
      colorfulDesign: false,
      marketplaceIntegration: false
    }
  },
  BASIC: {
    id: 'basic',
    name: 'Temel',
    price: 99,
    duration: 'monthly',
    features: [
      '100 sipariş/ay',
      '50 müşteri',
      'Gelişmiş raporlar',
      'Excel/PDF export',
      'Görsel PDF desteği',
      'Email destek',
      'Pazaryeri entegrasyonu (1 pazaryeri)'
    ],
    limits: {
      orders: 100,
      customers: 50,
      products: 500,
      invoices: 100
    },
    restrictions: {
      images: true,
      pdfImages: true,
      advancedReports: true,
      excelExport: true,
      colorfulDesign: true,
      marketplaceIntegration: true
    }
  },
  PROFESSIONAL: {
    id: 'professional',
    name: 'Profesyonel',
    price: 199,
    duration: 'monthly',
    features: [
      'Sınırsız sipariş',
      'Sınırsız müşteri',
      'Tüm raporlar',
      'Öncelikli destek',
      'API erişimi',
      'Çoklu kullanıcı',
      'Gelişmiş görseller',
      'Pazaryeri entegrasyonu (5 pazaryeri)'
    ],
    limits: {
      orders: -1, // unlimited
      customers: -1,
      products: -1,
      invoices: -1
    },
    restrictions: {
      images: true,
      pdfImages: true,
      advancedReports: true,
      excelExport: true,
      colorfulDesign: true,
      marketplaceIntegration: true
    }
  }
};

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usageStats, setUsageStats] = useState({
    orders: 0,
    customers: 0,
    products: 0,
    invoices: 0
  });

  const { currentUser, userData } = useAuth();

  // Create default free subscription
  const createDefaultSubscription = useCallback(async () => {
    if (!userData?.companyId) return;

    const defaultSubscription = {
      planId: 'free',
      status: 'active',
      createdAt: serverTimestamp(),
      expiresAt: null, // Free plan doesn't expire
      paymentMethod: null,
      lastPayment: null,
      companyId: userData.companyId
    };

    try {
      await setDoc(doc(db, 'subscriptions', userData.companyId), defaultSubscription);
      setSubscription(defaultSubscription);
    } catch (error) {
      console.error('Error creating default subscription:', error);
    }
  }, [userData?.companyId]);

  // Update subscription
  const updateSubscription = useCallback(async (planId, paymentData = null) => {
    if (!userData?.companyId) return false;

    const plan = SUBSCRIPTION_PLANS[planId.toUpperCase()];
    if (!plan) return false;

    const now = new Date();
    const expiryDate = planId === 'free' ? null : new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days

    const subscriptionUpdate = {
      planId: plan.id,
      status: 'active',
      updatedAt: serverTimestamp(),
      expiresAt: expiryDate,
      ...(paymentData && {
        lastPayment: {
          ...paymentData,
          date: serverTimestamp()
        }
      })
    };

    try {
      await updateDoc(doc(db, 'subscriptions', userData.companyId), subscriptionUpdate);
      return true;
    } catch (error) {
      console.error('Error updating subscription:', error);
      return false;
    }
  }, [userData?.companyId]);

  const isProcessingRef = React.useRef(false);

  useEffect(() => {
    const companyId = userData?.companyId;
    if (!currentUser || !companyId) {
      setLoading(false);
      return;
    }

    // Listen to subscription changes
    const subscriptionRef = doc(db, 'subscriptions', companyId);
    
    const unsubscribe = onSnapshot(subscriptionRef, (subscriptionSnap) => {
      if (isProcessingRef.current) return; // Prevent recursive loops from local latency-compensated writes
      
      if (!subscriptionSnap.exists()) {
        // No subscription found, create free subscription
        isProcessingRef.current = true;
        createDefaultSubscription().finally(() => {
          isProcessingRef.current = false;
          setLoading(false);
        });
        return;
      }

      const subscriptionData = subscriptionSnap.data();
      const now = new Date();
      const expiryDate = subscriptionData.expiresAt?.toDate ? subscriptionData.expiresAt.toDate() : null;

      if (expiryDate && expiryDate < now && subscriptionData.planId !== 'free') {
        // Subscription expired, downgrade to free
        isProcessingRef.current = true;
        updateSubscription('free').finally(() => {
          isProcessingRef.current = false;
          setLoading(false);
        });
      } else {
        setSubscription(subscriptionData);
        setLoading(false);
      }
    }, (error) => {
      console.error("Subscription listener error:", error);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, userData?.companyId]);

  // Track usage statistics from Firebase
  useEffect(() => {
    const companyId = userData?.companyId;
    if (!companyId) return;

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const monthStart = new Date(currentYear, currentMonth, 1);

    // Store unsubscribe functions
    const unsubscribers = [];

    // Track customers
    unsubscribers.push(onSnapshot(
      collection(db, 'companies', companyId, 'customers'),
      (snapshot) => {
        setUsageStats(prev => ({ ...prev, customers: snapshot.size }));
      },
      (error) => console.error("Customers listener error:", error)
    ));

    // Track products
    unsubscribers.push(onSnapshot(
      collection(db, 'companies', companyId, 'products'),
      (snapshot) => {
        setUsageStats(prev => ({ ...prev, products: snapshot.size }));
      },
      (error) => console.error("Products listener error:", error)
    ));

    // Track monthly orders
    const ordersQuery = query(
      collection(db, 'companies', companyId, 'orders'),
      where('orderDate', '>=', monthStart)
    );
    unsubscribers.push(onSnapshot(
      ordersQuery, 
      (snapshot) => {
        setUsageStats(prev => ({ ...prev, orders: snapshot.size }));
      },
      (error) => console.error("Orders listener error:", error)
    ));

    // Track monthly invoices
    const invoicesQuery = query(
      collection(db, 'companies', companyId, 'invoices'),
      where('invoiceDate', '>=', monthStart)
    );
    unsubscribers.push(onSnapshot(
      invoicesQuery, 
      (snapshot) => {
        setUsageStats(prev => ({ ...prev, invoices: snapshot.size }));
      },
      (error) => console.error("Invoices listener error:", error)
    ));

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [userData?.companyId]);

  // Cancel subscription
  const cancelSubscription = async () => {
    if (!userData?.companyId) return false;

    try {
      await updateDoc(doc(db, 'subscriptions', userData.companyId), {
        status: 'cancelled',
        cancelledAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      return false;
    }
  };

  // Check if feature is available
  const hasFeature = (feature) => {
    if (!subscription) return false;

    const plan = SUBSCRIPTION_PLANS[subscription.planId.toUpperCase()];
    return plan && plan.features.some(f => f.includes(feature));
  };

  // Check feature restrictions
  const hasRestriction = (restrictionType) => {
    if (!subscription) return true; // Default to restricted if no subscription

    const plan = SUBSCRIPTION_PLANS[subscription.planId.toUpperCase()];
    if (!plan || !plan.restrictions) return true;

    return plan.restrictions[restrictionType] === true;
  };

  // Check usage limits
  const checkUsageLimit = (type) => {
    if (!subscription) return { allowed: false, remaining: 0 };

    const plan = SUBSCRIPTION_PLANS[subscription.planId.toUpperCase()];
    if (!plan) return { allowed: false, remaining: 0 };

    const limit = plan.limits[type];
    const current = usageStats[type] || 0;

    if (limit === -1) {
      return { allowed: true, remaining: -1 }; // unlimited
    }

    return {
      allowed: current < limit,
      remaining: Math.max(0, limit - current),
      limit,
      current
    };
  };

  // Get current plan details
  const getCurrentPlan = () => {
    if (!subscription) return SUBSCRIPTION_PLANS.FREE;
    return SUBSCRIPTION_PLANS[subscription.planId.toUpperCase()] || SUBSCRIPTION_PLANS.FREE;
  };

  // Check if subscription is active
  const isActive = () => {
    if (!subscription) return false;

    if (subscription.status !== 'active') return false;

    if (subscription.expiresAt) {
      const now = new Date();
      const expiryDate = subscription.expiresAt.toDate ? subscription.expiresAt.toDate() : new Date(subscription.expiresAt);
      return expiryDate > now;
    }

    return true; // Free plan or no expiry
  };

  const value = {
    subscription,
    loading,
    usageStats,
    setUsageStats,
    updateSubscription,
    cancelSubscription,
    hasFeature,
    hasRestriction,
    checkUsageLimit,
    getCurrentPlan,
    isActive,
    plans: SUBSCRIPTION_PLANS
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}